import { useRef, useState, useEffect, useMemo } from "react";
import {
  Paper,
  Alert,
  Button,
  Grid,
  Box,
  CardContent,
  Typography,
  MenuList,
  ListItem,
  ListItemProps,
  Chip,
} from "@mui/material";
import { IAircraft, IAircraftGroup } from "@vmosolutions/oc-interfaces";
import AmplifyAPIWrapper from "src/API/AmplifyAPIWrapper";
import { useCancellablePromise } from "src/API/useCancellablePromise";
import { AircraftGroupForm } from "./AircraftGroupForm";
import { IActiveModal } from "src/pages/Admin/AdminWindow";
import { useDispatch } from "react-redux";
import { getCustomerStructures } from "src/redux/actions/Structure.Actions";
import { styled } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import LoadingWrapper from "src/components/LoadingWrapper/LoadingWrapper";

interface IManageAircraftProps {
  id: string;
  setActiveModal: (activeModal: IActiveModal | null) => void;
  height: number;
}

const ACGroupListItem: React.FC<ListItemProps> = styled((props: ListItemProps) => <ListItem {...props} />)(
  ({ theme }) => ({
    color: theme.palette.common.white,
    padding: "8px 12px",
    margin: "2px",
    cursor: "pointer",
    display: "flex",
    justifyContent: "space-between",
    " :hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  })
);

export const ManageAircraftGroup = (props: IManageAircraftProps): JSX.Element => {
  const { setActiveModal, height } = props;
  const { cancellablePromise } = useCancellablePromise();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const dispatch = useDispatch();
  const [aircraft, setAircraft] = useState<IAircraft[]>([]);

  useEffect(() => {
    const getAircraftAction = async (): Promise<void> => {
      //const notifications: INotifications = { dispatch, errorMessage: "No Aircraft found." };
      const response = await AmplifyAPIWrapper.Get<IAircraft[]>("/aircraft", undefined, cancellablePromise);
      setIsLoading(false);
      if (Array.isArray(response))
        setAircraft(
          response?.sort((a, b) => {
            return a.registration.toLowerCase().localeCompare(b.registration.toLowerCase());
          })
        );
    };
    getAircraftAction();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [aircraftGroupOptions, setAircraftGroupOptions] = useState<IAircraftGroup[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<IAircraftGroup | undefined>(undefined);

  useEffect(() => {
    const getGroups = async (): Promise<void> => {
      const response = await AmplifyAPIWrapper.Get<IAircraftGroup[]>("/aircraftGroup", undefined, cancellablePromise);
      if (Array.isArray(response)) {
        setAircraftGroupOptions(
          response.sort((a, b) => {
            return a.title.toLowerCase().localeCompare(b.title.toLowerCase());
          })
        );
      }
    };
    getGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateAircraftGroupList = (acGroup: IAircraftGroup, mode: "create" | "update" | "delete"): void => {
    const newAircraftGroupOptions = [...aircraftGroupOptions];
    const index = newAircraftGroupOptions.findIndex(
      (aircraftGroup: IAircraftGroup) => aircraftGroup._id === acGroup._id
    );
    switch (true) {
      case acGroup._id && mode === "create": // AircraftGroup Created
        newAircraftGroupOptions.push(acGroup);
        setSelectedGroup(acGroup);
        break;
      case acGroup._id && mode === "update" && index > -1: // AircraftGroup Updated
        newAircraftGroupOptions[index] = acGroup;
        setSelectedGroup(acGroup);
        break;
      case acGroup._id && mode === "delete" && index > -1: // AircraftGroup Deleted
        newAircraftGroupOptions.splice(index, 1);
        setSelectedGroup(undefined);
        break;
      default:
    }
    setAircraftGroupOptions(newAircraftGroupOptions);
    dispatch(getCustomerStructures());
  };

  return (
    <CardContent>
      <Grid container spacing={3} direction="row">
        <Grid item xs={3}>
          <Button
            variant="contained"
            color="success"
            size="small"
            fullWidth
            onClick={() => setSelectedGroup(undefined)}
            sx={{ mb: 3 }}
          >
            <AddIcon /> Add New Group
          </Button>
          <Paper sx={{ height: `calc(${height}px - 100px)`, overflowY: "scroll", overflowX: "hidden" }}>
            {isLoading ? (
              <Box sx={{ p: 2 }}>
                <LoadingWrapper message="Loading Aircraft Groups" />
              </Box>
            ) : (
              <MenuList>
                {aircraftGroupOptions.map((ac, acKey) => (
                  <ACGroupListItem
                    key={acKey}
                    onClick={() => {
                      setSelectedGroup(ac);
                    }}
                  >
                    <Box>{ac.title}</Box>
                    <Chip variant="filled" size="small" label={ac?.members?.length ?? 0} />
                  </ACGroupListItem>
                ))}
              </MenuList>
            )}
          </Paper>
        </Grid>

        <Grid item xs>
          <Typography variant="h5">Manage Aircraft Groups</Typography>

          <Grid container item>
            <Grid item xs={6}>
              <Alert severity="info" variant="filled">
                Inactive aircraft will not be shown on the flight display timeline.
              </Alert>
              <AircraftGroupForm
                selectedGroup={selectedGroup}
                updateAircraftGroupList={updateAircraftGroupList}
                setActiveModal={setActiveModal}
                aircraft={aircraft}
                setSelectedGroup={setSelectedGroup}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </CardContent>
  );
};
